import { useEffect, useRef } from 'react'
import type maplibregl from 'maplibre-gl'
import {
  LassoRejection,
  LngLat,
  pathToLine,
  pathToPolygon,
} from '@user/store/territory/lassoGeometry'
import { TerritoryBoundary } from '@user/store/territory/typings'
import { whenMapAlive } from './mapSafety'

export const TERRITORY_DRAFT_SOURCE = 'territory-draft'
export const TERRITORY_DRAFT_FILL_LAYER = 'territory-draft-fill'
export const TERRITORY_DRAFT_LINE_LAYER = 'territory-draft-line'
export const TERRITORY_DRAFT_CLOSING_LAYER = 'territory-draft-closing'

/** Deliberately not a territory colour: the draft is a tool, not a territory yet. */
const DRAFT_COLOR = '#11539D'

/**
 * Heavier than a saved territory's 0.1. The draft is the thing being worked on and is on screen
 * for seconds, not permanently.
 */
const DRAFT_FILL_OPACITY = 0.2

type Params = {
  map: maplibregl.Map | null
  active: boolean
  /**
   * True while the drawn shape is awaiting confirmation (the naming dialog is open).
   *
   * Distinct from `active` on purpose. Clearing `active` would tear the preview layers down and
   * the shape the user just drew would vanish from under the dialog while they name it. Locking
   * instead keeps it on screen and only stops a new stroke from starting.
   */
  locked: boolean
  onComplete: (boundary: TerritoryBoundary) => void
  /** Called instead of onComplete when the drawing could not become a territory. */
  onReject: (reason: LassoRejection) => void
  /**
   * What the stroke means.
   *
   * 'area' closes it into a territory boundary. 'line' leaves it open as a cut across an
   * existing territory -- a far smaller gesture than tracing a whole half, and the one people
   * reach for when dividing something.
   */
  mode?: 'area' | 'line'

  /** The completed cut, in 'line' mode. */
  onCompleteLine?: (line: LngLat[]) => void

  /** Fired when a new stroke begins, so any warning from the last one can clear itself. */
  onDrawStart: () => void
  /**
   * The browser or OS took the pointer away mid-stroke (a system gesture, a call, a dragged
   * notification). Reported so a half-drawn shape vanishing can be explained rather than just
   * looking like the map ignored the user.
   */
  onInterrupted: () => void
}

const EMPTY_COLLECTION: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
}

function setDraftData(
  map: maplibregl.Map,
  geometry: GeoJSON.Geometry | null,
  /**
   * The straight run from the current point back to the start, drawn dashed while the user is
   * still going. Without it the closing segment is invisible until release, which is how a
   * drawing that quietly crosses itself gets as far as the server.
   */
  closing?: LngLat[] | null,
): void {
  const source = map.getSource(TERRITORY_DRAFT_SOURCE) as
    | maplibregl.GeoJSONSource
    | undefined
  if (!source) return

  if (!geometry) {
    source.setData(EMPTY_COLLECTION)
    return
  }

  const features: GeoJSON.Feature[] = [
    { type: 'Feature', properties: { kind: 'path' }, geometry },
  ]

  if (closing && closing.length === 2) {
    features.push({
      type: 'Feature',
      properties: { kind: 'closing' },
      geometry: { type: 'LineString', coordinates: closing },
    })
  }

  source.setData({ type: 'FeatureCollection', features })
}

/**
 * Freehand lasso drawing for territory boundaries (WWM-17508, Feature A).
 *
 * The interaction is a single drag: press, draw a loop around the area, release. It is chosen
 * over click-by-click vertex placement because the people drawing territories are sales admins,
 * not mapping specialists -- "draw a circle around the neighbourhood" needs no explanation,
 * whereas placing vertices asks the user to think in polygons.
 *
 * Releasing closes the shape automatically, so there is no "click the first point again to
 * finish" step to discover or get wrong.
 *
 * While active this hook takes over the map's drag: dragPan and the rotate handlers are disabled
 * so that a drag draws instead of panning, and restored on exit. That is the reason draw mode is
 * explicitly entered and left rather than being always-on -- the map must still be a map.
 */
export function useTerritoryDraw({
  map,
  active,
  locked,
  mode = 'area',
  onComplete,
  onCompleteLine,
  onReject,
  onDrawStart,
  onInterrupted,
}: Params): void {
  // onComplete is read through a ref so that a caller passing an inline arrow does not tear the
  // listeners down and rebuild them on every render, which would drop an in-progress drag.
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const onRejectRef = useRef(onReject)
  onRejectRef.current = onReject
  const onDrawStartRef = useRef(onDrawStart)
  onDrawStartRef.current = onDrawStart
  const onInterruptedRef = useRef(onInterrupted)
  onInterruptedRef.current = onInterrupted
  const onCompleteLineRef = useRef(onCompleteLine)
  onCompleteLineRef.current = onCompleteLine

  // Read through a ref for the same reason as the callbacks: switching mode mid-session must
  // not rebuild the listeners and drop a stroke in progress.
  const modeRef = useRef(mode)
  modeRef.current = mode

  // Same reasoning for `locked`: it flips on every draw, and listing it as an effect dependency
  // would rebuild the listeners and remove the preview mid-flow.
  const lockedRef = useRef(locked)
  lockedRef.current = locked

  // Clear the finished shape once the dialog lets go, so the next drag starts on a clean map
  // rather than on top of the one that was just cancelled.
  useEffect(() => {
    if (!map || locked || !active) return
    if (!map.getSource(TERRITORY_DRAFT_SOURCE)) return

    setDraftData(map, null)
  }, [map, locked, active])

  useEffect(() => {
    if (!map || !active) return

    const canvas = map.getCanvas()

    // --- preview layers ---------------------------------------------------
    // Added on entry and removed on exit rather than living permanently in useMapLibreMarkers:
    // the draft only exists during a drag, and a stray empty source on every map is noise.
    if (!map.getSource(TERRITORY_DRAFT_SOURCE)) {
      map.addSource(TERRITORY_DRAFT_SOURCE, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
      })
    }
    if (!map.getLayer(TERRITORY_DRAFT_FILL_LAYER)) {
      map.addLayer({
        id: TERRITORY_DRAFT_FILL_LAYER,
        type: 'fill',
        source: TERRITORY_DRAFT_SOURCE,
        paint: {
          'fill-color': DRAFT_COLOR,
          'fill-opacity': DRAFT_FILL_OPACITY,
        },
      })
    }
    if (!map.getLayer(TERRITORY_DRAFT_LINE_LAYER)) {
      map.addLayer({
        id: TERRITORY_DRAFT_LINE_LAYER,
        type: 'line',
        source: TERRITORY_DRAFT_SOURCE,
        filter: ['!=', ['get', 'kind'], 'closing'],
        paint: { 'line-color': DRAFT_COLOR, 'line-width': 3 },
      })
    }
    if (!map.getLayer(TERRITORY_DRAFT_CLOSING_LAYER)) {
      map.addLayer({
        id: TERRITORY_DRAFT_CLOSING_LAYER,
        type: 'line',
        source: TERRITORY_DRAFT_SOURCE,
        filter: ['==', ['get', 'kind'], 'closing'],
        paint: {
          'line-color': DRAFT_COLOR,
          'line-width': 2,
          'line-dasharray': [2, 2],
          'line-opacity': 0.7,
        },
      })
    }

    // --- take over the drag -----------------------------------------------
    const panWasEnabled = map.dragPan.isEnabled()
    const rotateWasEnabled = map.dragRotate.isEnabled()
    const touchRotateWasEnabled = map.touchZoomRotate.isEnabled()

    map.dragPan.disable()
    map.dragRotate.disable()
    map.touchZoomRotate.disable()

    const previousCursor = canvas.style.cursor
    canvas.style.cursor = 'crosshair'

    // --- the drag itself ---------------------------------------------------
    let path: LngLat[] = []
    let drawing = false
    let pointerId: number | null = null

    // Read once per stroke, not per move. getBoundingClientRect forces layout, and calling it
    // on every pointermove meant a forced reflow 60+ times a second during the one interaction
    // that has to feel smooth. The canvas cannot move or resize mid-drag without a resize or
    // scroll, both of which refresh it below.
    let canvasRect = canvas.getBoundingClientRect()
    const refreshCanvasRect = () => {
      canvasRect = canvas.getBoundingClientRect()
    }

    // Screen position of the last point actually recorded, for the move threshold.
    let lastClientX = 0
    let lastClientY = 0

    /**
     * Ignore movements below this many CSS pixels.
     *
     * Every recorded point is re-serialised into the preview on each move, so an unfiltered
     * drag is quadratic in the number of points. A two-pixel floor removes the pointer jitter
     * that contributes nothing to the shape -- simplification would discard it anyway, but only
     * after it had already been stored and redrawn.
     */
    const MIN_MOVE_PX = 2

    const toLngLat = (event: PointerEvent): LngLat => {
      const { lng, lat } = map.unproject([
        event.clientX - canvasRect.left,
        event.clientY - canvasRect.top,
      ])
      // GeoJSON order. The server and TerritoryGeometry agree on [lng, lat]; MySQL's SRID 4326
      // is the opposite, which is handled server-side, not here.
      return [lng, lat]
    }

    const handlePointerDown = (event: PointerEvent) => {
      // Primary button only: a right-click is not a draw.
      if (event.button !== 0) return

      // A SECOND FINGER must not hijack the stroke. Touch fires pointerdown with button === 0
      // for every new contact, so the check above does not filter it -- without this guard,
      // resting a thumb on a tablet mid-drag reassigned pointerId and reset path, silently
      // destroying the drawing in progress. The comment here used to claim otherwise.
      if (drawing) return

      // A shape is already waiting to be named; the dialog owns the interaction until it closes.
      if (lockedRef.current) return

      drawing = true
      pointerId = event.pointerId
      canvasRect = canvas.getBoundingClientRect()
      lastClientX = event.clientX
      lastClientY = event.clientY
      path = [toLngLat(event)]

      // Clears any warning left by the previous attempt. Having it disappear the moment the
      // user tries again is why the bar needs no dismiss button -- and a dismiss button is
      // exactly what "Cancel" was being mistaken for.
      onDrawStartRef.current()

      // Keeps the stroke alive if the pointer leaves the canvas mid-drag, which is easy to do
      // when drawing out to the edge of the map.
      canvas.setPointerCapture(event.pointerId)
      event.preventDefault()
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (!drawing || event.pointerId !== pointerId) return

      if (
        Math.abs(event.clientX - lastClientX) < MIN_MOVE_PX &&
        Math.abs(event.clientY - lastClientY) < MIN_MOVE_PX
      ) {
        // Below the threshold: not part of the shape, and redrawing for it is pure cost.
        event.preventDefault()
        return
      }

      lastClientX = event.clientX
      lastClientY = event.clientY
      path.push(toLngLat(event))

      // Preview as a LineString while open -- a fill of a half-drawn loop looks like a mistake.
      // The dashed second feature shows where the shape will close, so the user can see the
      // run home and bring the pen back near the start instead of crossing their own path.
      setDraftData(
        map,
        { type: 'LineString', coordinates: path },
        // The dashed run home only means something for a shape. A cut is never closed, so
        // showing where it would close would describe something that will not happen.
        modeRef.current === 'line' || path.length <= 2
          ? null
          : [path[path.length - 1], path[0]],
      )
      event.preventDefault()
    }

    const finish = (event: PointerEvent) => {
      if (!drawing || event.pointerId !== pointerId) return

      drawing = false
      pointerId = null
      if (canvas.hasPointerCapture(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId)
      }

      if (modeRef.current === 'line') {
        const line = pathToLine(path)
        path = []

        if (!line) {
          // A tap is not a cut. Cleared quietly and the mode stays armed, same as a stray
          // tap in area mode.
          setDraftData(map, null)
          onRejectRef.current('too-small')
          return
        }

        // Left on screen while the dialog asks what to call the new territory, so the user can
        // see the cut they drew.
        setDraftData(map, { type: 'LineString', coordinates: line })
        onCompleteLineRef.current?.(line)
        return
      }

      const result = pathToPolygon(path)
      path = []

      if (!result.ok) {
        // Clear and stay in draw mode either way: the user keeps the map where it was and can
        // just draw again. The caller decides whether the reason is worth saying out loud --
        // a stray tap is not, a loop that crossed itself is.
        setDraftData(map, null)
        onRejectRef.current(result.reason)
        return
      }

      setDraftData(map, {
        type: 'Polygon',
        coordinates: result.boundary.coordinates,
      })
      onCompleteRef.current(result.boundary)
    }

    const handlePointerCancel = (event: PointerEvent) => {
      if (!drawing || event.pointerId !== pointerId) return

      // Enough points to have been a real attempt; anything shorter was a stray touch and
      // saying something about it would be noise.
      const wasSubstantial = path.length > 5

      drawing = false
      pointerId = null
      path = []
      setDraftData(map, null)

      if (canvas.hasPointerCapture(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId)
      }

      if (wasSubstantial) onInterruptedRef.current()
    }

    // The cached rect is invalidated by anything that can move the canvas mid-stroke.
    window.addEventListener('resize', refreshCanvasRect)
    window.addEventListener('scroll', refreshCanvasRect, true)

    canvas.addEventListener('pointerdown', handlePointerDown)
    canvas.addEventListener('pointermove', handlePointerMove)
    canvas.addEventListener('pointerup', finish)
    canvas.addEventListener('pointercancel', handlePointerCancel)

    return () => {
      window.removeEventListener('resize', refreshCanvasRect)
      window.removeEventListener('scroll', refreshCanvasRect, true)

      canvas.removeEventListener('pointerdown', handlePointerDown)
      canvas.removeEventListener('pointermove', handlePointerMove)
      canvas.removeEventListener('pointerup', finish)
      canvas.removeEventListener('pointercancel', handlePointerCancel)

      canvas.style.cursor = previousCursor

      // Everything from here reaches into the map, and on unmount the map is already gone:
      // HereMapNew calls useMapLibreInstance before this hook, React runs cleanups in declaration
      // order, so map.remove() has happened and getSource/getLayer reach through a style that no
      // longer exists. This is the exact failure mapSafety.ts documents and useTerritoryInteraction
      // already routes around; draw mode was left doing it unguarded, which is a throw in a
      // cleanup every time the map dialog closes with the pen still out.
      whenMapAlive(map, (live) => {
        if (panWasEnabled) live.dragPan.enable()
        if (rotateWasEnabled) live.dragRotate.enable()
        if (touchRotateWasEnabled) live.touchZoomRotate.enable()

        // Blank the source first. Removing layers is what actually clears the draft, but doing
        // this first guarantees no stale draft paints alongside the real territory that the
        // create response has just added to the map.
        if (live.getSource(TERRITORY_DRAFT_SOURCE)) setDraftData(live, null)

        // Guarded: the style can be swapped while draw mode is on (LayerControls), which takes the
        // layers with it, and removing an absent layer throws.
        if (live.getLayer(TERRITORY_DRAFT_FILL_LAYER)) {
          live.removeLayer(TERRITORY_DRAFT_FILL_LAYER)
        }
        if (live.getLayer(TERRITORY_DRAFT_LINE_LAYER)) {
          live.removeLayer(TERRITORY_DRAFT_LINE_LAYER)
        }
        if (live.getLayer(TERRITORY_DRAFT_CLOSING_LAYER)) {
          live.removeLayer(TERRITORY_DRAFT_CLOSING_LAYER)
        }
        if (live.getSource(TERRITORY_DRAFT_SOURCE)) {
          live.removeSource(TERRITORY_DRAFT_SOURCE)
        }
      })
    }
  }, [map, active])
}
